import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';

interface ItemFormData {
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
}

interface InvoiceItemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: string;
  currency: string;
  onSuccess?: () => void;
}

export function InvoiceItemModal({
  open,
  onOpenChange,
  invoiceId,
  currency,
  onSuccess,
}: InvoiceItemModalProps) {
  const { t } = useTranslation('invoices');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<ItemFormData>({
    defaultValues: {
      quantity: 1,
      unitPrice: 0,
      taxRate: 0,
    },
  });

  const addItemMutation = trpc.invoice.addItem.useMutation({
    onSuccess: () => {
      reset();
      onOpenChange(false);
      onSuccess?.();
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const watchedQuantity = watch('quantity') || 0;
  const watchedUnitPrice = watch('unitPrice') || 0;
  const watchedTaxRate = watch('taxRate') || 0;

  const subtotal = watchedQuantity * watchedUnitPrice;
  const taxAmount = (subtotal * watchedTaxRate) / 100;
  const total = subtotal + taxAmount;

  const onSubmit = (data: ItemFormData) => {
    setIsSubmitting(true);
    addItemMutation.mutate({
      invoiceId,
      description: data.description,
      quantity: data.quantity,
      unitPrice: data.unitPrice,
      taxRate: data.taxRate,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('itemModal.title')}</DialogTitle>
          <DialogDescription>
            {t('itemModal.description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">{t('itemModal.descriptionLabel')} *</Label>
            <Textarea
              id="description"
              {...register('description', { required: t('itemModal.descriptionRequired') })}
              placeholder={t('itemModal.descriptionPlaceholder')}
              rows={2}
            />
            {errors.description && (
              <p className="text-sm text-red-500">{errors.description.message}</p>
            )}
          </div>

          {/* Quantity and Unit Price */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="quantity">{t('itemModal.quantity')} *</Label>
              <Input
                id="quantity"
                type="number"
                min="1"
                step="1"
                {...register('quantity', {
                  required: t('itemModal.quantityRequired'),
                  valueAsNumber: true,
                  min: { value: 1, message: t('itemModal.quantityMin') },
                })}
              />
              {errors.quantity && (
                <p className="text-sm text-red-500">{errors.quantity.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="unitPrice">{t('itemModal.unitPrice', { currency })} *</Label>
              <Input
                id="unitPrice"
                type="number"
                step="0.01"
                min="0"
                {...register('unitPrice', {
                  required: t('itemModal.unitPriceRequired'),
                  valueAsNumber: true,
                  min: { value: 0, message: t('itemModal.priceMin') },
                })}
              />
              {errors.unitPrice && (
                <p className="text-sm text-red-500">{errors.unitPrice.message}</p>
              )}
            </div>
          </div>

          {/* Tax Rate */}
          <div className="space-y-2">
            <Label htmlFor="taxRate">{t('itemModal.taxRate')}</Label>
            <Input
              id="taxRate"
              type="number"
              step="0.01"
              min="0"
              max="100"
              {...register('taxRate', {
                valueAsNumber: true,
                min: { value: 0, message: t('itemModal.taxRateMin') },
                max: { value: 100, message: t('itemModal.taxRateMax') },
              })}
            />
            {errors.taxRate && (
              <p className="text-sm text-red-500">{errors.taxRate.message}</p>
            )}
          </div>

          {/* Summary */}
          <div className="rounded-lg bg-muted p-3">
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('lineItems.subtotal')}</span>
                <span>{formatCurrency(subtotal, currency)}</span>
              </div>
              {taxAmount > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('lineItems.tax')} ({watchedTaxRate}%)</span>
                  <span>{formatCurrency(taxAmount, currency)}</span>
                </div>
              )}
              <div className="flex justify-between border-t pt-1 font-medium">
                <span>{t('lineItems.total')}</span>
                <span>{formatCurrency(total, currency)}</span>
              </div>
            </div>
          </div>

          {/* Error Messages */}
          {addItemMutation.error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              {addItemMutation.error.message}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('itemModal.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('itemModal.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
